/**
 * A TÁR (#74) — „hol van az a papír?". A harmadik felület, és nem nő tovább.
 *
 * KÉT NÉZET, EGY DOBOZ (vászon: `design/tar-szelesites/`, Viktor 10-23):
 *
 *   - LISTA: bal oldalt a három szűrő (típus · időszak · partner), jobbra a
 *     keresés és a találatok, időszak szerint csoportosítva.
 *   - MEGNYITVA (`Enter`/`→`): a munkalista hasábjai — bal a lista, középen a
 *     papír ugyanabban a nézőben, jobbra a mezők. CSAK OLVASÁSRA: javítani és
 *     lefűzni a munkalistán lehet, különben ugyanaz a döntés két helyen dőlne
 *     el. `Esc` vissza.
 *
 * A doboz a munkalista osztályait viseli (`worklist`, `worklist__queue`, …):
 * ugyanaz a rács, ugyanaz a magasság, ugyanaz a papír-illesztés — nem egy
 * második, kicsit más változat belőle.
 */

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { docTypeLabel, formatAmount, formatDate, formatTaxNumber } from '@/i18n/generated';
import { api } from '@/lib/api-client';
import { DocumentOriginal } from '../worklist/DocumentOriginal';
import type { FieldsResponse } from '../worklist/fieldCorrection';
import { inDisplayOrder } from '../worklist/fieldCorrection';
import { useDocumentFile } from '../worklist/useDocumentFile';
import { periodHeading, periodLabel } from './period';
import type { ArchiveDocType, ArchiveItem, ArchiveState } from './useArchive';

const DOC_TYPES: ArchiveDocType[] = [
  'INBOUND_INVOICE',
  'OUTBOUND_INVOICE',
  'RECEIPT',
  'BANK_STATEMENT',
  'OTHER',
];

export function Archive({
  state,
  onPickCompany,
}: {
  state: ArchiveState;
  /** A cégválasztó nyitása (#73) — a Tár egy cégre szűr. */
  onPickCompany: () => void;
}) {
  const { t } = useTranslation();

  if (state.status === 'needCompany') {
    return (
      <div className="empty">
        <p>{t('archive.needCompany')}</p>
        <button type="button" onClick={onPickCompany}>
          {/* A kitöltött bordó gombon a globális világos `kbd`-doboz nem
              látszana — a „Lefűzöm" billentyűjelének mintája: a gomb színét
              veszi át. */}
          {t('archive.pickCompany')} <kbd className="worklist__file-key">c</kbd>
        </button>
      </div>
    );
  }

  return state.openId === null ? <ListView state={state} /> : <OpenView state={state} />;
}

// ── A LISTA ─────────────────────────────────────────────────────────

function ListView({ state }: { state: ArchiveState }) {
  const { t } = useTranslation();
  return (
    <div className="worklist archive">
      <section className="worklist__queue archive__filters" aria-label={t('archive.filters')}>
        <Filters state={state} />
      </section>
      <section className="archive__results" aria-label={t('archive.title')}>
        <Search state={state} />
        <Summary state={state} />
        <Results state={state} wide />
      </section>
    </div>
  );
}

/**
 * A SZŰRŐK BILLENTYŰI (Viktor, 10-26: „a filterekhez bal nyíllal"). A
 * keresőből a `←` ide hoz; itt `↑↓` lép a pipák és a mezők közt, a szóköz
 * pipál, a `→` és az `Enter` visszavisz a találatokra.
 *
 * A szövegmezőkben (időszak) a `←→` a kurzoré marad — ott csak a `↑↓` lép.
 * Minden itt kezelt billentyű megáll (`stopPropagation`): a Tár ablak-szintű
 * `↑↓`-ja különben a találatok közt is lépne, a `←` pedig a keresőbe ugrana.
 */
function filterKey(
  event: React.KeyboardEvent<HTMLElement>,
  container: HTMLElement | null,
  focusResults: () => void
): boolean {
  // A `Shift+↑↓` a szövegmezőben kijelöl — az a mezőé, nem a lépésé.
  if (container === null || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return false;
  }
  const target = event.target as HTMLElement;
  const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    const controls = [...container.querySelectorAll<HTMLInputElement>('input')];
    const at = controls.indexOf(target as HTMLInputElement);
    const next =
      controls[
        Math.min(controls.length - 1, Math.max(0, at + (event.key === 'ArrowDown' ? 1 : -1)))
      ];
    next?.focus();
    return true;
  }
  if (isCheckbox && (event.key === 'ArrowRight' || event.key === 'Enter')) {
    focusResults();
    return true;
  }
  // A pipán a `←` nem visz sehova — de a keresőbe se ugorjon.
  return isCheckbox && event.key === 'ArrowLeft';
}

function Filters({ state }: { state: ArchiveState }) {
  const { t } = useTranslation();
  const counts = new Map(state.facets?.docTypes.map((f) => [f.docType, f.count]) ?? []);
  const undated = state.facets?.periods.find((f) => f.period === null)?.count ?? 0;
  const body = useRef<HTMLDivElement>(null);

  const seen = useRef(state.filterRequest);
  useEffect(() => {
    if (state.filterRequest !== null && state.filterRequest !== seen.current) {
      seen.current = state.filterRequest;
      body.current?.querySelector<HTMLInputElement>('input')?.focus();
    }
  }, [state.filterRequest]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: a billentyűket a benne álló pipák és mezők kapják; ez csak a köztük való lépést fogja össze
    <div
      ref={body}
      className="archive__filter-body"
      onKeyDown={(event) => {
        if (filterKey(event, body.current, state.focusResults)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <h3 className="archive__filter-head">{t('archive.docType')}</h3>
      {DOC_TYPES.map((docType) => (
        <label key={docType} className="archive__check">
          <input
            type="checkbox"
            checked={state.docTypes.has(docType)}
            onChange={() => state.toggleDocType(docType)}
          />
          <span>{docTypeLabel(t, docType)}</span>
          <span className="archive__count">{counts.get(docType) ?? 0}</span>
        </label>
      ))}

      <h3 className="archive__filter-head">{t('archive.period')}</h3>
      <PeriodFields state={state} />
      <label className="archive__check">
        <input
          type="checkbox"
          checked={state.includeUndated}
          onChange={(event) => state.setIncludeUndated(event.target.checked)}
        />
        <span className="muted">{t('archive.includeUndated')}</span>
        <span className="archive__count">{undated}</span>
      </label>

      <h3 className="archive__filter-head">{t('archive.status')}</h3>
      <label className="archive__check">
        <input
          type="checkbox"
          checked={state.includeUnfiled}
          onChange={(event) => state.setIncludeUnfiled(event.target.checked)}
        />
        <span className="muted">{t('archive.includeUnfiled')}</span>
        <span className="archive__count">{state.unfiled}</span>
      </label>

      <h3 className="archive__filter-head">{t('archive.partner')}</h3>
      {(state.facets?.partners ?? []).map((partner) => (
        <label key={partner.partnerTaxNumber} className="archive__check">
          <input
            type="checkbox"
            checked={state.partner === partner.partnerTaxNumber}
            onChange={() =>
              state.setPartner(
                state.partner === partner.partnerTaxNumber ? null : partner.partnerTaxNumber
              )
            }
          />
          <span className="archive__partner">
            {partner.partnerName ?? partner.partnerTaxNumber}
          </span>
          <span className="archive__count">{partner.count}</span>
        </label>
      ))}

      <p className="worklist__keys archive__keys">
        <span>
          <kbd>/</kbd> <kbd>←</kbd> {t('archive.keys.search')}
        </span>
        <span>
          <kbd>i</kbd> {t('archive.keys.period')}
        </span>
        <span>
          <kbd>→</kbd> {t('archive.keys.open')}
        </span>
      </p>
    </div>
  );
}

/**
 * ETTŐL–EDDIG, BEÍRHATÓ MEZŐ (Viktor, 10-23: „mint a DOS-ban"). Alatta a
 * visszaigazolás: mit értettünk belőle — az elgépelés itt derül ki, nem egy
 * üres listából.
 */
function PeriodFields({ state }: { state: ArchiveState }) {
  const { t } = useTranslation();
  const fromField = useRef<HTMLInputElement>(null);

  const seen = useRef(state.periodRequest);
  useEffect(() => {
    if (state.periodRequest !== null && state.periodRequest !== seen.current) {
      seen.current = state.periodRequest;
      fromField.current?.focus();
      fromField.current?.select();
    }
  }, [state.periodRequest]);

  const invalid = state.from === 'invalid' || state.to === 'invalid';

  // A MEZŐBŐL A TALÁLATOKRA: `Enter` vagy `Esc` — a beírt időszak marad.
  // A `Tab` a következő mezőre visz, ahogy a böngészőben megszokott.
  const toResults = (event: React.KeyboardEvent) => {
    // A `↓`/`↑` a szűrők közt lép (`filterKey`), nem ide tartozik.
    if (event.key === 'Enter' || event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      state.focusResults();
    }
  };
  const echo = invalid
    ? t('archive.periodInvalid')
    : state.from === null && state.to === null
      ? t('archive.periodAll')
      : `${state.from === null ? '…' : periodLabel(state.from)} – ${
          state.to === null ? t('archive.periodNow') : periodLabel(state.to)
        }`;

  return (
    <div className="archive__period">
      <div className="archive__period-fields">
        <label>
          <span>{t('archive.from')}</span>
          <input
            ref={fromField}
            className="worklist__search-field num"
            value={state.fromText}
            placeholder="2026-08"
            aria-describedby="archive-period-echo"
            aria-invalid={state.from === 'invalid'}
            onChange={(event) => state.setFromText(event.target.value)}
            onKeyDown={toResults}
          />
        </label>
        <span className="archive__dash" aria-hidden="true">
          –
        </span>
        <label>
          <span>{t('archive.to')}</span>
          <input
            className="worklist__search-field num"
            value={state.toText}
            placeholder={t('archive.periodNowShort')}
            aria-describedby="archive-period-echo"
            aria-invalid={state.to === 'invalid'}
            onChange={(event) => state.setToText(event.target.value)}
            onKeyDown={toResults}
          />
        </label>
      </div>
      <p id="archive-period-echo" className={invalid ? 'archive__echo error' : 'archive__echo'}>
        {echo}
      </p>
      <p className="hint">{t('archive.periodHint')}</p>
    </div>
  );
}

function Search({ state }: { state: ArchiveState }) {
  const { t } = useTranslation();
  const field = useRef<HTMLInputElement>(null);
  const seen = useRef(state.searchRequest);
  useEffect(() => {
    if (state.searchRequest !== null && state.searchRequest !== seen.current) {
      seen.current = state.searchRequest;
      field.current?.focus();
      field.current?.select();
    }
  }, [state.searchRequest]);

  return (
    <div className="worklist__search">
      <input
        ref={field}
        type="search"
        className="worklist__search-field"
        aria-label={t('archive.searchLabel')}
        placeholder={t('archive.searchPlaceholder')}
        aria-keyshortcuts="/"
        value={state.query}
        onChange={(event) => state.setQuery(event.target.value)}
        onKeyDown={(event) => {
          // A MUNKALISTA KERESŐJÉNEK MINTÁJA (Viktor, 10-20: „nem lehetett
          // lemenni a listába"). Az `Esc` helyben él — kiszivárogva a
          // megnyitott bizonylatot is becsukná.
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            state.leaveSearch();
          }
          if (event.key === 'ArrowDown' || event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            state.focusResults();
          }
          // A `←` A MEZŐ ELEJÉN a szűrőkre visz (Viktor, 10-26). Szöveg
          // közben a kurzoré marad — a keresőben gépelni is kell.
          const field = event.currentTarget;
          if (
            event.key === 'ArrowLeft' &&
            field.selectionStart === 0 &&
            field.selectionEnd === 0 &&
            state.openId === null
          ) {
            event.preventDefault();
            event.stopPropagation();
            state.focusFilters();
          }
        }}
      />
      <span className="worklist__search-key" aria-hidden="true">
        /
      </span>
    </div>
  );
}

function Summary({ state }: { state: ArchiveState }) {
  const { t } = useTranslation();
  const filtered =
    state.docTypes.size > 0 ||
    state.partner !== null ||
    state.includeUnfiled ||
    state.fromText !== '' ||
    state.toText !== '';
  return (
    <p className="archive__summary">
      <span aria-live="polite">
        {state.status === 'loading'
          ? t('archive.loading')
          : state.status === 'failed'
            ? t('archive.failed')
            : t('archive.count', { count: state.total })}
      </span>
      {filtered ? (
        <button type="button" className="archive__clear" onClick={state.clearFilters}>
          {t('archive.clearFilters')}
        </button>
      ) : null}
    </p>
  );
}

/** A találatok, időszak szerint csoportosítva — a legújabb hónap elöl. */
function Results({ state, wide }: { state: ArchiveState; wide: boolean }) {
  const { t } = useTranslation();
  const groups = groupByPeriod(state.items);

  if (state.status === 'ready' && state.items.length === 0) {
    return (
      <>
        <p className="empty">{t('archive.noMatch')}</p>
        <UnfiledHint state={state} />
      </>
    );
  }

  return (
    <div className="worklist__groups">
      {groups.map(([period, items]) => (
        <div key={period ?? 'none'} className="worklist__group">
          <h3 className="worklist__group-head">
            <span className="worklist__group-name">
              {period === null ? t('archive.noPeriod') : periodHeading(period)}
            </span>
            <span className="worklist__group-count">{items.length}</span>
          </h3>
          <ul className="worklist__rows">
            {items.map((item) => (
              <Row key={item.id} item={item} state={state} wide={wide} />
            ))}
          </ul>
        </div>
      ))}
      {state.hasMore ? (
        <button type="button" className="archive__more" onClick={state.loadMore}>
          {t('archive.more')}
        </button>
      ) : null}
      <UnfiledHint state={state} />
    </div>
  );
}

/**
 * A TÁRBAN NEM TŰNHET EL PAPÍR (#95). Ha a lefűzött-szűrő egyező találatot
 * rejt el, a lista végén kimondjuk, hányat — a tegnap érkezett számlát kereső
 * könyvelő különben azt hinné, nincs meg. A lista végén áll, de MINDEN lapon:
 * a rejtett papírok jellemzően a legfrissebbek, tehát nem az utolsó lapon
 * lennének.
 */
function UnfiledHint({ state }: { state: ArchiveState }) {
  const { t } = useTranslation();
  if (state.status !== 'ready' || state.includeUnfiled || state.unfiled === 0) {
    return null;
  }
  return (
    <p className="archive__unfiled muted">
      {t('archive.unfiledHidden', { count: state.unfiled })}{' '}
      <button
        type="button"
        className="archive__clear"
        onClick={() => {
          // A gomb a kattintással eltűnik — a fókusz a találatokra megy, nem a semmibe.
          state.setIncludeUnfiled(true);
          state.focusResults();
        }}
      >
        {t('archive.showUnfiled')}
      </button>
    </p>
  );
}

function groupByPeriod(items: ArchiveItem[]): [string | null, ArchiveItem[]][] {
  const groups = new Map<string | null, ArchiveItem[]>();
  for (const item of items) {
    const list = groups.get(item.period) ?? [];
    list.push(item);
    groups.set(item.period, list);
  }
  return [...groups.entries()];
}

function amountOf(item: ArchiveItem): string {
  return item.grossAmount === null
    ? '—'
    : formatAmount(Number(item.grossAmount), { currency: item.currency ?? undefined });
}

function Row({ item, state, wide }: { item: ArchiveItem; state: ArchiveState; wide: boolean }) {
  const { t } = useTranslation();
  const current = item.id === state.focusedId;
  const row = useRef<HTMLLIElement | null>(null);

  // A KIJELÖLT SOR LÁTSZIK — a nyilakkal a lista alá is el lehet menni.
  useEffect(() => {
    if (current) row.current?.scrollIntoView?.({ block: 'nearest' });
  }, [current]);

  return (
    // A MUNKALISTA SORÁNAK MINTÁJA: roving tabindex, valódi fókusz — a
    // képernyőolvasó a `↑↓`-ra a sort olvassa fel. A kattintás megnyit; a
    // billentyűzeten ugyanezt az `Enter` teszi a regiszteren át.
    // biome-ignore lint/a11y/useKeyWithClickEvents: az `Enter` a regiszterben van (`useArchive`), nem a soron
    <li
      ref={(element) => {
        row.current = element;
        state.registerRow(item.id, element);
      }}
      tabIndex={current ? 0 : -1}
      className={wide ? 'worklist__row archive__row--wide' : 'worklist__row'}
      aria-current={current ? true : undefined}
      onFocus={() => state.focus(item.id)}
      onClick={() => state.open(item.id)}
    >
      <span className="row__text">
        <span className="row__partner">{item.partnerName ?? t('archive.noPartner')}</span>
        <span className="row__meta">
          {docTypeLabel(t, item.docType)}
          {item.issueDate === null ? '' : ` · ${formatDate(item.issueDate)}`}
          {item.invoiceNumber === null ? '' : ` · ${item.invoiceNumber}`}
        </span>
      </span>
      {wide ? (
        <span className="archive__tax num">
          {item.partnerTaxNumber === null ? '' : formatTaxNumber(item.partnerTaxNumber)}
        </span>
      ) : null}
      <span className="row__amount">{amountOf(item)}</span>
      {wide ? <span className="archive__state">{stateLabel(t, item)}</span> : null}
    </li>
  );
}

function stateLabel(t: (key: string) => string, item: ArchiveItem): string {
  if (item.status === 'FILED') return t('archive.state.filed');
  if (item.status === 'NOT_DEDUCTIBLE') return t('archive.state.notDeductible');
  return t('archive.state.waiting');
}

// ── A MEGNYITOTT BIZONYLAT ──────────────────────────────────────────

function OpenView({ state }: { state: ArchiveState }) {
  const { t } = useTranslation();
  const item = state.items.find((one) => one.id === state.openId) ?? null;
  const file = useDocumentFile(state.openId);

  return (
    <div className="worklist archive">
      <section className="worklist__queue" aria-label={t('archive.title')}>
        <Search state={state} />
        <Results state={state} wide={false} />
        <p className="worklist__keys">
          <span>
            <kbd>↑↓</kbd> {t('archive.keys.next')}
          </span>
          <span>
            <kbd>Esc</kbd> {t('archive.keys.back')}
          </span>
        </p>
      </section>

      <section className="worklist__detail" aria-label={t('archive.document')}>
        <div className="worklist__paper">
          {item === null ? null : <ArchiveHeader item={item} />}
          <DocumentOriginal state={file} onExit={state.close} />
        </div>
        <div className="worklist__side">
          <div className="worklist__side-body">
            <p>
              <span className="badge">{t('archive.readOnly')}</span>{' '}
              <span className="muted">{item === null ? '' : stateLabel(t, item)}</span>
            </p>
            {state.openId === null ? null : <ReadOnlyFields documentId={state.openId} />}
            <p className="hint">{t('archive.readOnlyHint')}</p>
          </div>
        </div>
      </section>
    </div>
  );
}

function ArchiveHeader({ item }: { item: ArchiveItem }) {
  const { t } = useTranslation();
  return (
    // A MUNKALISTA FEJLÉCÉNEK osztályai (`Worklist.tsx` `DocumentHeader`) —
    // ugyanaz a papír, ugyanaz a fejléc; csak a „Javítom" nincs rajta.
    <header className="document">
      <h2
        className={
          item.partnerName === null
            ? 'document__partner document__partner--empty'
            : 'document__partner'
        }
      >
        {item.partnerName ?? t('archive.noPartner')}
      </h2>
      <p className="document__meta">
        <span>{docTypeLabel(t, item.docType)}</span>
        {item.invoiceNumber === null ? null : <span>{item.invoiceNumber}</span>}
        {item.issueDate === null ? null : <span>{formatDate(item.issueDate)}</span>}
        {item.partnerTaxNumber === null ? null : (
          <span>{formatTaxNumber(item.partnerTaxNumber)}</span>
        )}
      </p>
      {item.grossAmount === null ? null : <p className="document__amount">{amountOf(item)}</p>}
    </header>
  );
}

/**
 * A MEZŐK, CSAK OLVASÁSRA — ugyanaz a forrás, mint a javító panelé
 * (`GET /documents/:id/fields`), ugyanabban a sorrendben (`inDisplayOrder`).
 */
function ReadOnlyFields({ documentId }: { documentId: string }) {
  const { t } = useTranslation();
  const [fields, setFields] = useState<FieldsResponse | 'failed' | null>(null);

  useEffect(() => {
    let live = true;
    setFields(null);
    api<FieldsResponse>(`/documents/${documentId}/fields`)
      .then((response) => {
        if (live) setFields(response);
      })
      // A HIBA KIMONDVA, nem örök „töltődik": az eltűnt panel hazudna.
      .catch(() => {
        if (live) setFields('failed');
      });
    return () => {
      live = false;
    };
  }, [documentId]);

  if (fields === null) {
    return <p className="muted">{t('archive.fieldsLoading')}</p>;
  }
  if (fields === 'failed') {
    return (
      <p className="error" role="alert">
        {t('archive.fieldsFailed')}
      </p>
    );
  }

  // `dt`/`dd` PÁROK közvetlenül a `dl`-ben — a `worklist__fields` rácsa két
  // hasábos, egy köztes `div` szétszedné.
  const rows: ReactNode[] = inDisplayOrder(fields.fields).flatMap((field) => [
    <dt key={`${field.fieldKey}-label`}>{t(`worklist.correction.label.${field.fieldKey}`)}</dt>,
    <dd key={`${field.fieldKey}-value`} className="num">
      {field.effectiveValue ?? '—'}
    </dd>,
  ]);
  return <dl className="worklist__fields">{rows}</dl>;
}
